/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from "axios";
import { createContext, ReactNode, useContext } from "react";
import { useGeneralSettings } from "./General";
import {
  collabDataProps,
  monetizationDataProps,
} from "@/Pages/Dashboard/Influencer/BuildProfile/BuildProfileProps";

// interfaces ---------------------------
interface InfluencerProviderProps {
  children: React.ReactNode;
}

export type YearWrap2024MetricsData = {
  metrics: {
    total_likes: number;
    total_characters: number;
    total_comments: number;
    total_impressions: number;
    totalPosts: number;
  };
  most_favorable: {
    month: {
      month_number: number;
      total_engagement: number;
    };
    day: {
      date: string; // ISO date string
      impressions: number;
    };
    day_of_week: {
      day_number: number;
      avg_engagement: number;
    };
  };
  most_engaged_posts: {
    post_type: string; // e.g., "text", "image", etc.
    post_id: string; // assuming it's a string (MongoDB-like ID)
    post_url: string;
    total_engagement: number;
    likes: number;
    comments: number;
    reposts: number;
    post_date: string; // ISO date string
  }[];
  longest_streak: {
    duration: number;
    start_date: string; // ISO date string
    end_date: string; // ISO date string
  };
  monthlyTrend: {
    _id: number; // month number
    avgEngagement: number;
    totalEngagement: number;
    totalPost: number;
  }[];
  text: {
    likes: string;
    comments: string;
    month: string;
    monthlyTrendHeader: string;
    monthlyTrendSubHeader: string;
    monthlyTrendFooter: string;
  };
};

export type InfluencerEarningType = {
  overall: {
    totalEarnings: number;
    totalWithdrawn: number;
    totalBalance: number;
  };
  minWithdrawLimit: number;
  pendingWithdrawRequest: false;
};

export type ReferredInfluencerType = {
  _id: string;
  name: string;
  profileImage: string;
  isClaimed: boolean;
  claimedDate: string;
  rewardAmount: number;
  referDate: string;
};

export type GetReferralRewardResultType = {
  rewardAmount: number;
};

export type GetCollabEarningsTypes = {
  amount: number;
  campaignInfo: {
    name: string;
    startDate: string;
  };
  brandInfo: {
    companyName: string;
  };
  collabId: string;
  creditDate: string;
  tid: string;
};

export type AllEarningWithdrawRequestsType = {
  requestDate: Date;
  amount: number;
  status: string;
  transactionId: string;
};

interface InfluencerContextProps {
  updatePersonalInfo: (data: object) => Promise<{
    success: any;
    any;
  }>;

  updateCollabInfo: (data: [collabDataProps]) => Promise<{
    success: any;
    any;
  }>;
  updateMonetizationInfo: (data: monetizationDataProps[]) => Promise<{
    success: any;
    any;
  }>;
  updateContentInfo: (data: object) => Promise<{
    success: any;
    any;
  }>;
  updateAudienceInfo: (data: object) => Promise<{
    success: any;
    any;
  }>;
  updateChargesInfo: (data: object) => Promise<{
    success: any;
    any;
  }>;
  updateWishlist: (data: object) => Promise<{ success: any }>;
  uploadInfluencerImages: (data: object) => Promise<{
    success: any;
    result: any;
    any;
  }>;
  uploadCompanyImages: (data: object) => Promise<{
    success: any;
    result: any;
    any;
  }>;
  saveTempBrandData: (data: object) => Promise<{
    success: any;
    any;
  }>;
  SaveUserInfo: (data: object) => Promise<{
    success: any;
    error: ReactNode;
    any;
  }>;
  SentMessageFromSNS: (number: string) => Promise<{
    MessageID: any;
    code: number;
    any;
  }>;
  getPersonalInfo: () => Promise<{
    data2: any;
    data3: any;
    data4: any;
    data5: any;
    data6: {
      PostMaxPrice: number;
      PostMinPrice: number;
      MonthlyNumberOfPost: number;
      MonthlyMaxPrice: number;
      MonthlyMinPrice: number;
      YearlyNumberOfPost: number;
      YearlyMaxPrice: number;
      YearlyMinPrice: number;
      AcceptFreeBies: boolean;
      isNegotiable: boolean;
    };
    success: boolean;
    data?: object;
  }>;
  getInfluencerInfoFromSlug: (slug: string) => Promise<{
    error(error: any): unknown;
    success: boolean;
    data?: { referal_code: any };
    data1?: object;
    data2?: { data: [] };
    data3?: { data: any };
    data4?: { categories: []; formats: [] };
    data5?: { platforms: []; types: []; topLinks: [] };
  }>;
  getTempInfluencerLinkedinProfileFromSlug: (slug: string) => Promise<{
    error(error: any): unknown;
    success: boolean;
    LinkedinAnalysis: {
      avgLikes: number;
      avgComments: number;
      avgEngagement: number;
      avgEngagementPercent: any;
      avgReposts: number;
      followers: number;
      topThreeEngagedPosts: [];
      name: string;
      gender: string;
      tagline: string;
      location: string;
      profile_image: string;
      experience: string;
      badge: string;
      featured: [];
      profile_url: string;
      firstThirtyLikes: number[];
      firstThirtyComments: number[];
      topLinks: [];
    };
    ContentCategory: string[];
    audienceCategory: [];
  }>;
  getFeaturedCreators: () => Promise<{ success: boolean; topInfluencer?: [] }>;
  getBrandWishlist: () => Promise<{
    success: boolean;
    data?: { companyName: string; companyUrl: string }[];
  }>;
  getSubscriptionPlans: () => Promise<{ success: boolean; AllPlans?: [] }>;
  getAnalytics: () => Promise<{
    success: boolean;
    dealEarning?: [];
    revenueEarning?: [];
    numberOfUnlocks?: [];
    numberOfReferrals?: [];
    totalViews?: number;
  }>;
  getLeaderboard: () => Promise<{
    success: boolean;
    leaderboard?: {
      topInfluencer: [];
      yourPosition: {
        name: "";
        rank: 0;
        slug: "";
      };
    };
  }>;
  getAllAnchorsCollab: () => Promise<{
    success: boolean;
    collabs: [];
    Error?: string;
  }>;
  getAllAnchorsCollabDetails: (id: string) => Promise<{
    success: boolean;
    collabDetails: any[];
    Error?: string;
  }>;
  updateAnchorsCollabDetails: (
    id: string,
    data: object
  ) => Promise<{
    success: boolean;
    collabDetails: [];
    Error?: string;
  }>;
  submitCollabRating: (data: {
    collabId: string;
    rating: number;
    review: string;
    categories: {
      earningsRating: string;
      creativeFreedom: string;
    };
  }) => Promise<{ success: boolean; error: string }>;
  updateTopLinksInfo: (data: object) => Promise<{
    success: any;
    any;
  }>;
  getInfluencerAllCollabKudosBySlug: (slug: string) => Promise<{
    success: boolean;
    data: [];
  }>;
  removeKudosFromCollab: (collabId: string) => Promise<{
    success: boolean;
  }>;
  giveKudosToCollab: (
    collabId: string,
    source: string
  ) => Promise<{
    success: boolean;
  }>;
  followInfluencer: (
    influencerId: string,
    source: string
  ) => Promise<{
    success: boolean;
  }>;
  unfollowInfluencer: (influencerId: string) => Promise<{
    success: boolean;
  }>;
  uploadInfluCollabImage: (data: object) => Promise<{
    success: any;
    result: any;
    any;
  }>;
  uploadInfluCollabVideos: (data: object) => Promise<{
    success: any;
    result: any;
    any;
  }>;
  uploadInfluencerLinkedinData: (data: object) => Promise<{
    success: any;
    result: any;
    any;
  }>;
  getChapterDetails: ({
    chapterId,
    geoCoordinates,
    geoLocation,
    ipAddress,
  }: {
    chapterId: string;
    geoCoordinates: any;
    geoLocation: any;
    ipAddress: string;
  }) => Promise<any>;
  getInfluencerExactPrice: (payload: object) => Promise<{
    success: boolean;
    data: {
      name: string;
      profile_image: string;
      followers: string;
      tagline: string;
      static_CPI: number;
      expectedMaxImpression: number;
      expectedSpendAmount: number;
      minExpectedSpendAmount: number;
      maxExpectedSpendAmount: number;
    };
  }>;
  getChapterDetailsV2: ({
    chapterSlug,
    geoCoordinates,
    geoLocation,
    ipAddress,
  }: {
    chapterSlug: string;
    geoCoordinates: {
      permission: boolean;
      latitude: number;
      longitude: number;
    };
    geoLocation: {
      city: string;
      state: string;
      country: string;
      postalCode: string;
      latitude: number;
      longitude: number;
    };
    ipAddress: string;
  }) => Promise<any>;
  getYearWrap2024: () => Promise<{
    success: boolean;
    message: string;
    result: YearWrap2024MetricsData;
  }>;
  getEarnings: () => Promise<{
    success: boolean;
    message?: string;
    result: InfluencerEarningType;
  }>;
  getReferrals: () => Promise<{
    success: boolean;
    message?: string;
    result: ReferredInfluencerType[];
  }>;
  getReferralReward: ({ referToId }: { referToId: string }) => Promise<{
    success: boolean;
    message?: string;
    result: GetReferralRewardResultType;
  }>;
  getCollabEarnings: () => Promise<{
    success: boolean;
    message?: string;
    result?: GetCollabEarningsTypes[];
    error?: any;
  }>;
  requestWithdrawEarnings: ({ amount }: { amount: number }) => Promise<{
    success: boolean;
    message?: string;
    result?: any;
    error?: any;
  }>;
  getEarningWithdrawRequests: () => Promise<{
    success: boolean;
    message?: string;
    result?: AllEarningWithdrawRequestsType[];
    error?: any;
  }>;
  uploadCreatorInvoices: (data) => Promise<{
    success: boolean;
    result: { Location: string };
    error: string;
  }>;

  saveInfluencerBankDetails: (data) => Promise<{
    success: boolean;
    newInfluencerBankDetails?: any;
    error?: any;
  }>;
  getLatestInfluencerBankDetails: () => Promise<{
    success: boolean;
    latestInfluencerBankDetails?: any;
    error?: any;
  }>;
}

const InfluencerContext = createContext<InfluencerContextProps | null>(null);
const host: string = import.meta.env.VITE_BACKEND_SERVER_URL;

export const useInfluencer = () => {
  return useContext(InfluencerContext);
};

export const InfluencerProvider: React.FC<InfluencerProviderProps> = (
  props
) => {
  const generalState = useGeneralSettings();

  //get featured creators
  const getFeaturedCreators = async () => {
    try {
      const response = await axios.get(
        `${host}/influencer/getFeaturedCreators`,
        {
          headers: {
            jwtToken: localStorage.getItem("jwtToken"),
          },
        }
      );

      // Check if the response contains the LinkedIn authorization URL
      if (response.status !== 200) {
        return { Error: response.data.error };
      } else {
        return response.data;
      }
    } catch (error) {
      return { Error: error.response.data.error };
    }
  };

  // update personal info -----------------------
  const updatePersonalInfo = async (data: object) => {
    try {
      const response = await axios.post(
        `${host}/influencer/updatePersonalData`,
        { ...data, chipsCredit: generalState.SidebarChipsCount.profile },
        {
          headers: {
            jwtToken: localStorage.getItem("jwtToken"),
          },
        }
      );

      // Check if the response contains the LinkedIn authorization URL
      if (response.status !== 200) {
        throw new Error("Error in saving the data");
      } else {
        return response.data;
      }
    } catch (error) {
      alert(error);
    }
  };

  // update personal info -----------------------
  const updateCollabInfo = async (data: [collabDataProps]) => {
    try {
      const response = await axios.post(
        `${host}/influencer/updateCollabHistory`,
        { data, chipsCredit: generalState.SidebarChipsCount.collab },
        {
          headers: {
            jwtToken: localStorage.getItem("jwtToken"),
          },
        }
      );

      // Check if the response contains the LinkedIn authorization URL
      if (response.status !== 200) {
        throw new Error("Error in saving the data");
      } else {
        return response.data;
      }
    } catch (error) {
      alert(error);
    }
  };

  // update personal info -----------------------
  const updateMonetizationInfo = async (data: []) => {
    try {
      const response = await axios.post(
        `${host}/influencer/updateMonetizationHistory`,
        { data, chipsCredit: generalState.SidebarChipsCount.monetization },
        {
          headers: {
            jwtToken: localStorage.getItem("jwtToken"),
          },
        }
      );

      // Check if the response contains the LinkedIn authorization URL
      if (response.status !== 200) {
        throw new Error("Error in saving the data");
      } else {
        return response.data;
      }
    } catch (error) {
      alert(error);
    }
  };

  // update personal info -----------------------
  const updateContentInfo = async (data: object) => {
    try {
      const response = await axios.post(
        `${host}/influencer/updateContentInfo`,
        { ...data, chipsCredit: generalState.SidebarChipsCount.content },
        {
          headers: {
            jwtToken: localStorage.getItem("jwtToken"),
          },
        }
      );

      // Check if the response contains the LinkedIn authorization URL
      if (response.status !== 200) {
        throw new Error("Error in saving the data");
      } else {
        return response.data;
      }
    } catch (error) {
      alert(error);
    }
  };

  // update personal info -----------------------
  const updateAudienceInfo = async (data: object) => {
    try {
      const response = await axios.post(
        `${host}/influencer/updateAudienceInfo`,
        { ...data, chipsCredit: generalState.SidebarChipsCount.audience },
        {
          headers: {
            jwtToken: localStorage.getItem("jwtToken"),
          },
        }
      );

      // Check if the response contains the LinkedIn authorization URL
      if (response.status !== 200) {
        throw new Error("Error in saving the data");
      } else {
        return response.data;
      }
    } catch (error) {
      alert(error);
    }
  };
  const updateChargesInfo = async (data: object) => {
    try {
      const response = await axios.post(
        `${host}/influencer/updateChargesInfo`,
        { ...data, chipsCredit: generalState.SidebarChipsCount.charges },
        {
          headers: {
            jwtToken: localStorage.getItem("jwtToken"),
          },
        }
      );

      // Check if the response contains the LinkedIn authorization URL
      if (response.status !== 200) {
        throw new Error("Error in saving the data");
      } else {
        return response.data;
      }
    } catch (error) {
      alert(error);
    }
  };

  // update personal info -----------------------
  const updateWishlist = async (data: object) => {
    try {
      const response = await axios.post(
        `${host}/influencer/updateWishlist`,
        { data },
        {
          headers: {
            jwtToken: localStorage.getItem("jwtToken"),
          },
        }
      );

      // Check if the response contains the LinkedIn authorization URL
      if (response.status !== 200) {
        throw new Error("Error in saving the data");
      } else {
        return response.data;
      }
    } catch (error) {
      throw new Error(error);
    }
  };

  // update personal info -----------------------
  const saveTempBrandData = async (data: object) => {
    try {
      const response = await axios.post(`${host}/influencer/saveBrandData`, {
        ...data,
      });

      // Check if the response contains the LinkedIn authorization URL
      if (response.status !== 200) {
        throw new Error("Error in saving the data");
      } else {
        return response.data;
      }
    } catch (error) {
      alert("Error in saving the data, Please try again !!!");
    }
  };

  // get personal info -----------------------
  const getPersonalInfo = async () => {
    try {
      const response = await axios.get(`${host}/influencer/getPersonalData`, {
        headers: {
          jwtToken: localStorage.getItem("jwtToken"),
        },
      });

      // Check if the response contains the LinkedIn authorization URL
      if (response.status !== 200) {
        throw new Error("Error in saving the data");
      } else {
        return response.data;
      }
    } catch (error) {
      alert(error);
    }
  };

  // get Analytics
  const getAnalytics = async () => {
    try {
      const response = await axios.get(`${host}/influencer/getAnalytics`, {
        headers: {
          jwtToken: localStorage.getItem("jwtToken"),
        },
      });
      // Check if the response contains the LinkedIn authorization URL
      if (response.status !== 200) {
        throw new Error("Error in saving the data");
      } else {
        return response.data;
      }
    } catch (error) {
      alert(error);
    }
  };

  // get personal info -----------------------
  const getInfluencerInfoFromSlug = async (slug: string) => {
    try {
      const response = await axios.get(
        `${host}/influencer/getPersonalDataFromSlug?slug=${slug}`,
        {
          headers: {
            jwtToken: localStorage.getItem("jwtToken"),
          },
        }
      );

      // Check if the response contains the LinkedIn authorization URL
      if (response.status !== 200) {
        throw new Error("Error in fetching the Page");
      } else {
        return response.data;
      }
    } catch (error) {
      alert(error);
    }
  };

  const getTempInfluencerLinkedinProfileFromSlug = async (slug: string) => {
    try {
      const response = await axios.get(
        `${host}/influencer/linkedin/temporary/profile?slug=${slug}`
      );

      // Check if the response contains the LinkedIn authorization URL
      if (response.status !== 200) {
        throw new Error("Error in fetching the Page");
      } else {
        return response.data;
      }
    } catch (error) {
      throw new Error(error);
    }
  };

  // get personal info -----------------------
  const getBrandWishlist = async () => {
    try {
      const response = await axios.get(`${host}/influencer/getBrandWishlist`, {
        headers: {
          jwtToken: localStorage.getItem("jwtToken"),
        },
      });

      // Check if the response contains the LinkedIn authorization URL
      if (response.status !== 200) {
        throw new Error("Error in saving the data");
      } else {
        return response.data;
      }
    } catch (error) {
      alert(error);
    }
  };

  // get personal info -----------------------
  const uploadInfluencerImages = async (data) => {
    try {
      const response = await axios.post(`${host}/upload/influencerdata`, data, {
        headers: {
          jwtToken: localStorage.getItem("jwtToken"),
        },
      });

      // Check if the response contains the LinkedIn authorization URL
      if (response.status !== 200) {
        throw new Error("Error in saving the data");
      } else {
        return response.data;
      }
    } catch (error) {
      alert(error);
    }
  };

  // get personal info -----------------------
  const uploadCompanyImages = async (data) => {
    try {
      const response = await axios.post(`${host}/upload/companydata`, data, {
        headers: {
          jwtToken: localStorage.getItem("jwtToken"),
        },
      });

      // Check if the response contains the LinkedIn authorization URL
      if (response.status !== 200) {
        throw new Error("Error in saving the data");
      } else {
        return response.data;
      }
    } catch (error) {
      alert(error);
    }
  };

  // Update the user info --------------------------
  const SaveUserInfo = async (data) => {
    try {
      const response = await axios.post(`${host}/influencer/saveInfo`, data, {
        headers: {
          jwtToken: localStorage.getItem("jwtToken"),
        },
      });

      // Check if the response contains the LinkedIn authorization URL
      if (response.status !== 200) {
        throw new Error("Error in saving the data");
      } else {
        return response.data;
      }
    } catch (error) {
      alert(error);
    }
  };

  const SentMessageFromSNS = async (number) => {
    try {
      const response = await axios.get(
        `${host}/influencer/sendMsg?message=Mobile Number&number=${number}&subject=Anchors`,
        {
          headers: {
            jwtToken: localStorage.getItem("jwtToken"),
          },
        }
      );

      // Check if the response contains the LinkedIn authorization URL
      if (response.status !== 200) {
        throw new Error("Error in saving the data");
      } else {
        return response.data;
      }
    } catch (error) {
      alert(error);
    }
  };

  const getSubscriptionPlans = async () => {
    try {
      const response = await axios.get(
        `${host}/influencer/Subscription/getAllPlan`,
        {
          headers: {
            jwtToken: localStorage.getItem("jwtToken"),
          },
        }
      );
      if (response.status !== 200) {
        return { Error: response.data.error };
      } else {
        return response.data;
      }
    } catch (e) {
      return { Error: e.response.data.error };
    }
  };

  // get leaderboard -----------------------
  const getLeaderboard = async () => {
    try {
      const response = await axios.get(`${host}/influencer/leaderboard`, {
        headers: {
          jwtToken: localStorage.getItem("jwtToken"),
        },
      });

      // Check if the response is authorized
      if (response.status !== 200) {
        return { Error: response.data.error };
      } else {
        return response.data;
      }
    } catch (error) {
      return { Error: error.response.data.error };
    }
  };

  //anchor collab routes
  const getAllAnchorsCollab = async () => {
    try {
      const response = await axios.get(
        `${host}/collab/influencer/getAllCollab`,
        {
          headers: {
            jwtToken: localStorage.getItem("jwtToken"),
          },
        }
      );
      if (response.status !== 200) {
        return { Error: response.data.error };
      } else {
        return response.data;
      }
    } catch (e) {
      return { Error: e.response.data.error };
    }
  };
  const getAllAnchorsCollabDetails = async (id: string) => {
    try {
      const response = await axios.get(
        `${host}/collab/influencer/getCollabDetails/?collabId=${id} `,
        {
          headers: {
            jwtToken: localStorage.getItem("jwtToken"),
          },
        }
      );
      if (response.status !== 200) {
        return { Error: response.data.error };
      } else {
        return response.data;
      }
    } catch (e) {
      return { Error: e.response.data.error };
    }
  };
  const submitCollabRating = async (data) => {
    try {
      const response = await axios.post(
        `${host}/collab/influencer/rating`,
        data,
        {
          headers: {
            jwtToken: localStorage.getItem("jwtToken"),
          },
        }
      );
      if (response.status !== 200) {
        return { Error: response.data.error };
      } else {
        return response.data;
      }
    } catch (e) {
      return { Error: e.response.data.error };
    }
  };
  const updateAnchorsCollabDetails = async (id: string, data: object) => {
    try {
      const response = await axios.post(
        `${host}/collab/influencer/updateCollab/${id} `,
        data,
        {
          headers: {
            jwtToken: localStorage.getItem("jwtToken"),
          },
        }
      );
      if (response.status !== 200) {
        return { Error: response.data.error };
      } else {
        return response.data;
      }
    } catch (e) {
      return { Error: e.response.data.error };
    }
  };

  const uploadInfluCollabImage = async (data) => {
    try {
      const response = await axios.post(
        `${host}/upload/InfluCollabImageData`,
        data,
        {
          headers: {
            jwtToken: localStorage.getItem("jwtToken"),
          },
        }
      );

      // Check if the response contains the LinkedIn authorization URL
      if (response.status !== 200) {
        throw new Error("Error in saving the data");
      } else {
        return response.data;
      }
    } catch (error) {
      alert(error);
    }
  };

  const uploadInfluCollabVideos = async (data) => {
    try {
      const response = await axios.post(
        `${host}/upload/InfluCollabVideoData`,
        data,
        {
          headers: {
            jwtToken: localStorage.getItem("jwtToken"),
          },
        }
      );

      // Check if the response contains the LinkedIn authorization URL
      if (response.status !== 200) {
        throw new Error("Error in saving the data");
      } else {
        return response.data;
      }
    } catch (error) {
      alert(error);
    }
  };
  const updateTopLinksInfo = async (data: object) => {
    try {
      const response = await axios.post(
        `${host}/influencer/updateTopLinks`,
        { payload: data },
        {
          headers: {
            jwtToken: localStorage.getItem("jwtToken"),
          },
        }
      );

      // Check if the response contains the LinkedIn authorization URL
      if (response.status !== 200) {
        throw new Error("Error in saving the data");
      } else {
        return response.data;
      }
    } catch (error) {
      alert(error);
    }
  };
  // feed
  const getInfluencerAllCollabKudosBySlug = async (slug: string) => {
    try {
      const response = await axios.get(
        `${host}/influencer/collabKudos?slug=${slug}`,
        {
          headers: {
            jwtToken: localStorage.getItem("jwtToken"),
          },
        }
      );

      if (response.status !== 200) {
        throw new Error("Error in fetching the Kudos data");
      } else {
        return response.data;
      }
    } catch (error) {
      alert(error);
    }
  };

  const removeKudosFromCollab = async (collabId: string) => {
    try {
      const response = await axios.post(
        `${host}/influencer/post/unKudos`,
        { collabId },
        {
          headers: {
            jwtToken: localStorage.getItem("jwtToken"),
          },
        }
      );

      if (response.status !== 200) {
        throw new Error("Error in removing the Kudos");
      } else {
        return response.data;
      }
    } catch (error) {
      alert(error);
    }
  };

  const giveKudosToCollab = async (collabId: string, source: string) => {
    try {
      const response = await axios.post(
        `${host}/influencer/post/kudos`,
        { collabId, source },
        {
          headers: {
            jwtToken: localStorage.getItem("jwtToken"),
          },
        }
      );

      if (response.status !== 200) {
        throw new Error("Error in giving the Kudos");
      } else {
        return response.data;
      }
    } catch (error) {
      alert(error);
    }
  };

  const followInfluencer = async (influencerId: string, source: string) => {
    try {
      const response = await axios.post(
        `${host}/influencer/followCreator`,
        { creatorId: influencerId, source },
        {
          headers: {
            jwtToken: localStorage.getItem("jwtToken"),
          },
        }
      );

      if (response.status !== 200) {
        throw new Error("Error in following the influencer");
      } else {
        return response.data;
      }
    } catch (error) {
      alert(error);
    }
  };

  const unfollowInfluencer = async (influencerId: string) => {
    try {
      const response = await axios.post(
        `${host}/influencer/unfollowCreator`,
        { creatorId: influencerId },
        {
          headers: {
            jwtToken: localStorage.getItem("jwtToken"),
          },
        }
      );

      if (response.status !== 200) {
        throw new Error("Error in unfollowing the influencer");
      } else {
        return response.data;
      }
    } catch (error) {
      alert(error);
    }
  };

  const uploadInfluencerLinkedinData = async (data) => {
    try {
      const response = await axios.post(
        `${host}/upload/influencerLinekdinData`,
        data,
        {
          headers: {
            jwtToken: localStorage.getItem("jwtToken"),
          },
        }
      );

      // Check if the response contains the LinkedIn authorization URL
      if (response.status !== 200) {
        throw new Error("Error in saving the data");
      } else {
        return response.data;
      }
    } catch (error) {
      alert(error);
    }
  };
  //chapters
  const getChapterDetails = async ({
    chapterId,
    geoCoordinates,
    geoLocation,
    ipAddress,
  }: {
    chapterId: string;
    geoCoordinates: any;
    geoLocation: any;
    ipAddress: string;
  }) => {
    try {
      const res = await axios.get(`${host}/influencer/${chapterId}/chapter`, {
        params: {
          ip: ipAddress,
          lat: geoCoordinates?.permission
            ? geoCoordinates?.latitude
            : geoLocation?.latitude,
          lon: geoCoordinates?.permission
            ? geoCoordinates?.longitude
            : geoLocation?.longitude,
          city: geoLocation?.city,
          state: geoLocation?.state,
          country: geoLocation?.country,
          postalCode: geoLocation?.postalCode,
          geoLocation: geoCoordinates?.permission,
        },
        headers: {
          jwtToken: localStorage.getItem("jwtToken"),
          geo: btoa(JSON.stringify(geoCoordinates)),
        },
      });
      return res?.data;
    } catch (error) {
      console.log(error);
      return { error: error.response.data.error };
    }
  };

  const getChapterDetailsV2 = async ({
    chapterSlug,
    geoCoordinates,
    geoLocation,
    ipAddress,
  }: {
    chapterSlug: string;
    geoCoordinates: any;
    geoLocation: any;
    ipAddress: string;
  }) => {
    try {
      const res = await axios.get(`${host}/influencer/chapter/${chapterSlug}`, {
        params: {
          ip: ipAddress,
          lat: geoCoordinates?.permission
            ? geoCoordinates?.latitude
            : geoLocation?.latitude,
          lon: geoCoordinates?.permission
            ? geoCoordinates?.longitude
            : geoLocation?.longitude,
          city: geoLocation?.city,
          state: geoLocation?.state,
          country: geoLocation?.country,
          postalCode: geoLocation?.postalCode,
          geoLocation: geoCoordinates?.permission,
        },
        headers: {
          jwtToken: localStorage.getItem("jwtToken"),
          "x-geo": !!geoCoordinates?.permission,
        },
      });
      return res?.data;
    } catch (error) {
      console.log(error);
      return { success: false, error: error.response.data.error };
    }
  };

  const getInfluencerExactPrice = async ({ username, payload }) => {
    try {
      const response: any = await axios.post(
        `${host}/influencer/calculatedPrice/${username}`,
        { ...payload }
      );
      return response?.data;
    } catch (error) {
      console.log(error);
    }
  };

  //Year Wrap 2024
  const getYearWrap2024 = async () => {
    try {
      const response: any = await axios.get(
        `${host}/influencer/yearWrap/2024`,
        {
          headers: {
            jwtToken: localStorage.getItem("jwtToken"),
          },
        }
      );
      return response?.data;
    } catch (error) {
      console.log(error);
      return { success: false, error: error.response.data.error };
    }
  };

  // referral earning system
  const getEarnings = async () => {
    try {
      const response = await axios.get(`${host}/influencer/earning`, {
        headers: {
          jwtToken: localStorage.getItem("jwtToken"),
        },
      });
      return response.data;
    } catch (error) {
      return { error: error.response.data.error };
    }
  };

  const getReferrals = async () => {
    try {
      const response = await axios.get(`${host}/influencer/getReferralList`, {
        headers: {
          jwtToken: localStorage.getItem("jwtToken"),
        },
      });
      return response.data;
    } catch (error) {
      return { error: error.response.data.error };
    }
  };

  const getReferralReward = async ({ referToId }: { referToId: string }) => {
    try {
      const response = await axios.post(
        `${host}/influencer/referralReward/scratch/${referToId}/getReward`,
        {},
        {
          headers: {
            jwtToken: localStorage.getItem("jwtToken"),
          },
        }
      );
      return response.data;
    } catch (error) {
      return { error: error.response.data.error };
    }
  };

  const getCollabEarnings = async () => {
    try {
      const response = await axios.get(`${host}/influencer/getCollabEarning`, {
        headers: {
          jwtToken: localStorage.getItem("jwtToken"),
        },
      });
      return response.data;
    } catch (error) {
      return { error: error.response.data.error };
    }
  };

  const requestWithdrawEarnings = async ({ amount }: { amount: number }) => {
    try {
      const response = await axios.post(
        `${host}/influencer/earning/withdraw`,
        {
          amount: amount,
        },
        {
          headers: {
            jwtToken: localStorage.getItem("jwtToken"),
          },
        }
      );

      return response.data;
    } catch (error) {
      return { error: error.response.data.error };
    }
  };

  const getEarningWithdrawRequests = async () => {
    try {
      const response = await axios.get(`${host}/influencer/earning/withdraw`, {
        headers: {
          jwtToken: localStorage.getItem("jwtToken"),
        },
      });
      return response.data;
    } catch (error) {
      return { error: error.response.data.error };
    }
  };

  const uploadCreatorInvoices = async (data) => {
    try {
      const response = await axios.post(
        `${host}/upload/CreatorInvoicesData`,
        data,
        {
          headers: {
            jwtToken: localStorage.getItem("jwtTokenD"),
          },
        }
      );
      return response.data;
    } catch (error) {
      return { error: error.response.data.error };
    }
  };

  //Bank details
  const saveInfluencerBankDetails = async (data) => {
    try {
      const response = await axios.post(
        `${host}/influencer/bank-details`,
        data,
        {
          headers: {
            jwtToken: localStorage.getItem("jwtToken"),
          },
        }
      );
      return response.data;
    } catch (error) {
      return { error: error.response.data.error };
    }
  };

  const getLatestInfluencerBankDetails = async () => {
    try {
      const response = await axios.get(
        `${host}/influencer/bank-details/latest`,
        {
          headers: {
            jwtToken: localStorage.getItem("jwtToken"),
          },
        }
      );
      return response.data;
    } catch (error) {
      return { error: error.response.data.error };
    }
  };
  return (
    <InfluencerContext.Provider
      value={{
        updatePersonalInfo,
        getPersonalInfo,
        updateCollabInfo,
        updateMonetizationInfo,
        updateAudienceInfo,
        updateContentInfo,
        updateChargesInfo,
        uploadInfluencerImages,
        uploadCompanyImages,
        updateWishlist,
        getBrandWishlist,
        SaveUserInfo,
        SentMessageFromSNS,
        getInfluencerInfoFromSlug,
        saveTempBrandData,
        getFeaturedCreators,
        getSubscriptionPlans,
        getAnalytics,
        getLeaderboard,
        getAllAnchorsCollab,
        getAllAnchorsCollabDetails,
        updateAnchorsCollabDetails,
        submitCollabRating,
        updateTopLinksInfo,
        getInfluencerAllCollabKudosBySlug,
        removeKudosFromCollab,
        giveKudosToCollab,
        followInfluencer,
        unfollowInfluencer,
        getTempInfluencerLinkedinProfileFromSlug,
        uploadInfluCollabImage,
        uploadInfluCollabVideos,
        uploadInfluencerLinkedinData,
        getChapterDetails,
        getInfluencerExactPrice,
        getChapterDetailsV2,
        getYearWrap2024,
        getEarnings,
        getReferrals,
        getReferralReward,
        getCollabEarnings,
        requestWithdrawEarnings,
        getEarningWithdrawRequests,
        uploadCreatorInvoices,

        //payment details
        saveInfluencerBankDetails,
        getLatestInfluencerBankDetails,
      }}
    >
      {props?.children}
    </InfluencerContext.Provider>
  );
};
